import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import niaLogo from '../Assets/Logo.png';
import philippinesSeal from '../Assets/Bagong_Pilipinas_logo.png';

// Define empty template rows (20 empty rows for user to fill)
const initialTemplateRows = Array.from({ length: 20 }, (_, i) => ({
  id: i + 1,
  num: '',
  ia_name: '',
  cis_name: '',
  districtNum: '',
  municipality: '',
  mailing_address: '',
  president: '',
  contactNum: '',
  email_address: '',
  dateOrganized: '',
  sec_RegistrationNum: '',
  sec_DateApproved: '',
  tin: '',
  water_permitNum: '',
  water_dateApproved: '',
  membership_fb: '',
  membership_actual: '',
  sectorNum: '',
  serviceArea: '',
  fusa: '',
  operational: '',
  date_moaSigned: '',
  f_source: '',
  projCost: '',
  date_turnOver: '',
  ia_existContract: '',
  imt_dateSigned: '',
  imt_undercontract: '',
  canalLength_lined: '',
  canalLength_unlined: '',
  total_CanalLength: '',
  fundSource: '',
  category: '',
  devolutionPhases: '',
  idoAssigned: '',
  remarks: '',
}));

function CISTemplate({ darkMode }) {
  const [year, setYear] = useState('2026');
  const [searchTerm, setSearchTerm] = useState('');
  const [templateRows, setTemplateRows] = useState(initialTemplateRows);
  const [headerImo, setHeaderImo] = useState(localStorage.getItem('headerImo') || 'BOHOL-CEBU-SIQUIJOR IMO');
  const [headerProfileDate, setHeaderProfileDate] = useState(localStorage.getItem('headerProfileDate') || 'CIS IA PROFILE as of March 31, 2025');
  const [headerRegion, setHeaderRegion] = useState(localStorage.getItem('headerRegion') || 'Region: 7');
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [showAllData, setShowAllData] = useState(false);
  const fileInputRef = useRef(null);

  // Handle input change for any field
  const handleInputChange = (rowIndex, field, value) => {
    setTemplateRows(prev =>
      prev.map((row, i) =>
        i === rowIndex ? { ...row, [field]: value } : row
      )
    );
  };

  // Save header edits
  const saveHeader = () => {
    localStorage.setItem('headerImo', headerImo);
    localStorage.setItem('headerProfileDate', headerProfileDate);
    localStorage.setItem('headerRegion', headerRegion);
    setIsEditingHeader(false);
  };

  // Export to Excel — matching CISbohol format
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('CIS Template');

    // Add logos if available
    try {
      const niaLogoBuffer = await fetch(niaLogo).then(res => res.arrayBuffer());
      const philippinesSealBuffer = await fetch(philippinesSeal).then(res => res.arrayBuffer());
      
      const niaLogoId = workbook.addImage({
        buffer: new Uint8Array(niaLogoBuffer),
        extension: 'png',
      });
      const philippinesSealId = workbook.addImage({
        buffer: new Uint8Array(philippinesSealBuffer),
        extension: 'png',
      });
      
      worksheet.addImage(niaLogoId, {
        tl: { col: 0, row: 0 },
        ext: { width: 80, height: 80 },
      });
      worksheet.addImage(philippinesSealId, {
        tl: { col: 2, row: 0 },
        ext: { width: 80, height: 80 },
      });
    } catch (error) {
      console.warn('Could not load logo images:', error);
    }

    // Header rows
    worksheet.mergeCells('A3:AI3');
    worksheet.getCell('A3').value = 'Republic of the Philippines';
    worksheet.getCell('A3').font = { name: 'Calibri', size: 18, bold: true };
    
    worksheet.mergeCells('A4:AI4');
    worksheet.getCell('A4').value = 'NATIONAL IRRIGATION ADMINISTRATION';
    worksheet.getCell('A4').font = { name: 'Calibri', size: 16, bold: true };
    
    worksheet.mergeCells('A5:AI5');
    worksheet.getCell('A5').value = 'REGIONAL OFFICE NO. VII (CENTRAL VISAYAS)';
    worksheet.getCell('A5').font = { name: 'Calibri', size: 16, bold: true };
    
    worksheet.mergeCells('A6:AI6');
    worksheet.getCell('A6').value = 'BOHOL-CEBU-SIQUIJOR IRRIGATION MANAGEMENT OFFICE';
    worksheet.getCell('A6').font = { name: 'Calibri', size: 16, bold: true };
    
    worksheet.mergeCells('A7:AI7');
    worksheet.getCell('A7').value = 'BOHOL-SIQUIJOR OFFICE';
    worksheet.getCell('A7').font = { name: 'Calibri', size: 16, color: { argb: '87CEEB' } };
    
    worksheet.mergeCells('A8:AI8');
    worksheet.getCell('A8').value = headerImo;
    worksheet.getCell('A8').font = { name: 'Calibri', size: 14 };
    
    worksheet.mergeCells('A9:AI9');
    worksheet.getCell('A9').value = headerProfileDate;
    worksheet.getCell('A9').font = { name: 'Calibri', size: 14 };
    
    worksheet.mergeCells('A10:AI10');
    worksheet.getCell('A10').value = headerRegion;
    worksheet.getCell('A10').font = { name: 'Calibri', size: 14 };
    
    worksheet.mergeCells('A11:AI11');
    worksheet.getCell('A11').value = 'PROVINCE 1: BOHOL';
    worksheet.getCell('A11').font = { name: 'Calibri', size: 14 };
    
    worksheet.addRow([]);

    // Main headers
    const mainHeaders = [
      'ACTIONS', 'NO', 'NAME OF IA', 'NO', 'NAME OF CIS', 'DISTRICT NO.', 'MUNICIPALITY', 'MAILING ADDRESS', 'NAME OF PRESIDENT',
      'CONTACT NUMBER', 'EMAIL ADDRESS', 'DATE IA ORGANIZED', 'SEC REGISTRATION', 'SEC REGISTRATION', 'TAX IDENTIFICATION NUMBER (TIN)',
      'WATER PERMIT', 'WATER PERMIT', 'MEMBERSHIP', 'MEMBERSHIP', 'sectorNum', 'serviceArea', 'fusa', 'operational',
      'ORIGINAL CONSTRUCTION', 'ORIGINAL CONSTRUCTION', 'ORIGINAL CONSTRUCTION', 'ORIGINAL CONSTRUCTION', 'ia_existContract',
      'IMT CONTRACT', 'IMT CONTRACT', 'canalLength_lined', 'canalLength_unlined', 'total_CanalLength', 'FUNDSOURCE', 'CATEGORY',
      'PHASES FOR DEVOLUTION', 'IDO ASSIGNED', 'REMARKS',
    ];
    
    const mainHeaderRow = worksheet.addRow(mainHeaders);
    mainHeaderRow.eachCell((cell, colNumber) => {
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2E7D32' } };
      cell.alignment = { horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: '000' } },
        bottom: { style: 'thin', color: { argb: '000' } },
        left: { style: 'thin', color: { argb: '000' } },
        right: { style: 'thin', color: { argb: '000' } },
      };
      
      // Merge cells for grouped headers
      if ([13, 15, 17, 23, 29, 31].includes(colNumber)) {
        if (colNumber === 13) worksheet.mergeCells(`M${mainHeaderRow.number}:N${mainHeaderRow.number}`);
        if (colNumber === 15) worksheet.mergeCells(`P${mainHeaderRow.number}:Q${mainHeaderRow.number}`);
        if (colNumber === 17) worksheet.mergeCells(`R${mainHeaderRow.number}:S${mainHeaderRow.number}`);
        if (colNumber === 23) worksheet.mergeCells(`X${mainHeaderRow.number}:AA${mainHeaderRow.number}`);
        if (colNumber === 29) worksheet.mergeCells(`AC${mainHeaderRow.number}:AD${mainHeaderRow.number}`);
        if (colNumber === 31) worksheet.mergeCells(`AF${mainHeaderRow.number}:AG${mainHeaderRow.number}`);
      }
    });

    // Sub headers
    const subHeaders = [
      '', '', '', '', '', '', '', '', '', '', '', '', 'REGISTRATION NO.', 'DATE APPROVED', '',
      'PERMIT NO.', 'DATE APPROVED', 'membership_fb', 'membership_actual', '', '', '', '',
      'DATE OF MOA SIGNED', 'F-SOURCE', 'projCost', 'DATE OF TURNOVER', '',
      'DATE OF SIGNING', 'imt_undercontract', '', '', '', '', '', '', '', '',
    ];
    
    const subHeaderRow = worksheet.addRow(subHeaders);
    subHeaderRow.eachCell((cell, colNumber) => {
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4CAF50' } };
      cell.alignment = { horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: '000' } },
        bottom: { style: 'thin', color: { argb: '000' } },
        left: { style: 'thin', color: { argb: '000' } },
        right: { style: 'thin', color: { argb: '000' } },
      };
    });

    // Add empty data rows
    templateRows.forEach(row => {
      const dataRow = worksheet.addRow([
        '', // ACTIONS
        row.num || '',
        row.ia_name || '',
        row.num || '',
        row.cis_name || '',
        row.districtNum || '',
        row.municipality || '',
        row.mailing_address || '',
        row.president || '',
        row.contactNum || '',
        row.email_address || '',
        row.dateOrganized || '',
        row.sec_RegistrationNum || '',
        row.sec_DateApproved || '',
        row.tin || '',
        row.water_permitNum || '',
        row.water_dateApproved || '',
        row.membership_fb || '',
        row.membership_actual || '',
        row.sectorNum || '',
        row.serviceArea || '',
        row.fusa || '',
        row.operational || '',
        row.date_moaSigned || '',
        row.f_source || '',
        row.projCost || '',
        row.date_turnOver || '',
        row.ia_existContract || '',
        row.imt_dateSigned || '',
        row.imt_undercontract || '',
        row.canalLength_lined || '',
        row.canalLength_unlined || '',
        row.total_CanalLength || '',
        row.fundSource || '',
        row.category || '',
        row.devolutionPhases || '',
        row.idoAssigned || '',
        row.remarks || '',
      ]);
      
      dataRow.eachCell(cell => {
        cell.border = {
          top: { style: 'thin', color: { argb: '000' } },
          bottom: { style: 'thin', color: { argb: '000' } },
          left: { style: 'thin', color: { argb: '000' } },
          right: { style: 'thin', color: { argb: '000' } },
        };
      });
    });

    // Footer rows (yellow)
    const subtotalRow = worksheet.addRow(['Subtotal: new projects (not in the inventory yet)']);
    worksheet.mergeCells(`A${worksheet.lastRow.number}:AI${worksheet.lastRow.number}`);
    subtotalRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
      cell.border = {
        top: { style: 'thin', color: { argb: '000' } },
        bottom: { style: 'thin', color: { argb: '000' } },
        left: { style: 'thin', color: { argb: '000' } },
        right: { style: 'thin', color: { argb: '000' } },
      };
    });

    const grandTotalRow = worksheet.addRow(['Grand Total: All']);
    worksheet.mergeCells(`A${worksheet.lastRow.number}:AI${worksheet.lastRow.number}`);
    grandTotalRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
      cell.border = {
        top: { style: 'thin', color: { argb: '000' } },
        bottom: { style: 'thin', color: { argb: '000' } },
        left: { style: 'thin', color: { argb: '000' } },
        right: { style: 'thin', color: { argb: '000' } },
      };
    });

    // Set column widths
    worksheet.columns = [
      { key: 'actions', width: 5 },
      { key: 'num', width: 5 },
      { key: 'ia_name', width: 30 },
      { key: 'num2', width: 5 },
      { key: 'cis_name', width: 25 },
      { key: 'districtNum', width: 10 },
      { key: 'municipality', width: 25 },
      { key: 'mailing_address', width: 25 },
      { key: 'president', width: 25 },
      { key: 'contactNum', width: 15 },
      { key: 'email_address', width: 20 },
      { key: 'dateOrganized', width: 15 },
      { key: 'sec_RegistrationNum', width: 15 },
      { key: 'sec_DateApproved', width: 15 },
      { key: 'tin', width: 15 },
      { key: 'water_permitNum', width: 15 },
      { key: 'water_dateApproved', width: 15 },
      { key: 'membership_fb', width: 10 },
      { key: 'membership_actual', width: 10 },
      { key: 'sectorNum', width: 10 },
      { key: 'serviceArea', width: 15 },
      { key: 'fusa', width: 10 },
      { key: 'operational', width: 15 },
      { key: 'date_moaSigned', width: 15 },
      { key: 'f_source', width: 15 },
      { key: 'projCost', width: 15 },
      { key: 'date_turnOver', width: 15 },
      { key: 'ia_existContract', width: 15 },
      { key: 'imt_dateSigned', width: 15 },
      { key: 'imt_undercontract', width: 15 },
      { key: 'canalLength_lined', width: 10 },
      { key: 'canalLength_unlined', width: 10 },
      { key: 'total_CanalLength', width: 15 },
      { key: 'fundSource', width: 15 },
      { key: 'category', width: 15 },
      { key: 'devolutionPhases', width: 15 },
      { key: 'idoAssigned', width: 15 },
      { key: 'remarks', width: 20 },
    ];

    // Set row heights
    worksheet.getRow(1).height = 60;
    worksheet.getRow(2).height = 60;
    worksheet.getRow(3).height = 30;
    worksheet.getRow(4).height = 25;
    worksheet.getRow(5).height = 25;
    worksheet.getRow(6).height = 25;
    worksheet.getRow(7).height = 25;
    worksheet.getRow(8).height = 20;
    worksheet.getRow(9).height = 20;
    worksheet.getRow(10).height = 20;
    worksheet.getRow(11).height = 20;

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `CIS_Template_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Handle file import selection
  const handleImportFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      alert('File selected: ' + file.name + '. In a real app, you would process this file.');
      event.target.value = null;
    }
  };

  // Handle manage data actions
  const handleManageData = (value) => {
    if (value === 'import') {
      fileInputRef.current.click();
    } else if (value === 'export') {
      exportToExcel();
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.href = '/';
  };

  // Scroll sync effect
  useEffect(() => {
    const scrollContent = document.getElementById('scrollContent');
    const bottomScroll = document.getElementById('bottomScroll');
    if (scrollContent && bottomScroll) {
      const onBottomScroll = () => {
        scrollContent.scrollLeft = bottomScroll.scrollLeft;
      };
      const onContentScroll = () => {
        bottomScroll.scrollLeft = scrollContent.scrollLeft;
      };
      bottomScroll.addEventListener('scroll', onBottomScroll);
      scrollContent.addEventListener('scroll', onContentScroll);
      return () => {
        bottomScroll.removeEventListener('scroll', onBottomScroll);
        scrollContent.removeEventListener('scroll', onContentScroll);
      };
    }
  }, []);

  // Filter data (for future use)
  const filteredData = templateRows.filter(item =>
    Object.values(item).some(val =>
      (val?.toString()?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    )
  );

  return (
    <Container darkMode={darkMode} isSidebarExpanded={true} showRightPanel={false}>
      <TopControls>
        <BackButton onClick={handleBack}>Back</BackButton>
      </TopControls>
      <HeaderContainer>
        <HeaderLeft>
          <HeaderText>
            <ProfileText>
              {isEditingHeader ? (
                <>
                  <input value={headerImo} onChange={(e) => setHeaderImo(e.target.value)} style={{ width: '300px', marginBottom: '5px' }} />
                  <input value={headerProfileDate} onChange={(e) => setHeaderProfileDate(e.target.value)} style={{ width: '300px', marginBottom: '5px' }} />
                  <input value={headerRegion} onChange={(e) => setHeaderRegion(e.target.value)} style={{ width: '300px', marginBottom: '5px' }} />
                  <button onClick={saveHeader} style={{ marginRight: '5px' }}>Save</button>
                  <button onClick={() => setIsEditingHeader(false)}>Cancel</button>
                </>
              ) : (
                <>
                  <h2>{headerImo}</h2>
                  <p>{headerProfileDate}</p>
                  <p>{headerRegion}</p>
                  <p>PROVINCE 1: BOHOL</p>
                  {/* <button onClick={() => setIsEditingHeader(true)}>Edit</button> */}
                </>
              )}
            </ProfileText>
          </HeaderText>
        </HeaderLeft>
        <RightControls>
          {/* <SearchInput
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          /> */}
          {/* <StyledButton>Add CIS Bohol</StyledButton> */}
          {/* <ManageDataSelect onChange={(e) => handleManageData(e.target.value)}>
            <option value="">Manage Data</option>
            <option value="import">Import</option>
            <option value="export">Export</option>
          </ManageDataSelect>
          <input
            type="file"
            accept=".xlsx"
            onChange={handleImportFileSelect}
            style={{ display: 'none' }}
            ref={fileInputRef}
          /> */}
        </RightControls>
      </HeaderContainer>

      <TableContainer>
        <TableWrapper id="scrollContent">
          <Table darkMode={darkMode}>
            <thead>
              {/* reuse existing table header structure */}
              <tr>
                <TableHeader darkMode={darkMode}>ACTIONS</TableHeader>
                <TableHeader darkMode={darkMode}>NO</TableHeader>
                <TableHeader darkMode={darkMode}>NAME OF IA</TableHeader>
                <TableHeader darkMode={darkMode}>NO</TableHeader>
                <TableHeader darkMode={darkMode}>NAME OF CIS</TableHeader>
                <TableHeader darkMode={darkMode}>DISTRICT NO.</TableHeader>
                <TableHeader darkMode={darkMode}>MUNICIPALITY</TableHeader>
                <TableHeader darkMode={darkMode}>MAILING ADDRESS</TableHeader>
                <TableHeader darkMode={darkMode}>NAME OF PRESIDENT</TableHeader>
                <TableHeader darkMode={darkMode}>CONTACT NUMBER</TableHeader>
                <TableHeader darkMode={darkMode}>EMAIL ADDRESS</TableHeader>
                <TableHeader darkMode={darkMode}>DATE IA ORGANIZED</TableHeader>
                <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                  SEC REGISTRATION
                </TableHeader>
                <TableHeader darkMode={darkMode}>TAX IDENTIFICATION NUMBER (TIN)</TableHeader>
                <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                  WATER PERMIT
                </TableHeader>
                <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                  MEMBERSHIP
                </TableHeader>
                <TableHeader darkMode={darkMode}>sectorNum</TableHeader>
                <TableHeader darkMode={darkMode}>serviceArea</TableHeader>
                <TableHeader darkMode={darkMode}>fusa</TableHeader>
                <TableHeader darkMode={darkMode}>operational</TableHeader>
                <TableHeader darkMode={darkMode} colSpan="4" style={{ textAlign: 'center' }}>
                  ORIGINAL CONSTRUCTION
                </TableHeader>
                <TableHeader darkMode={darkMode}>ia_existContract</TableHeader>
                <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                  IMT CONTRACT
                </TableHeader>
                <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                  CANAL LENGTH
                </TableHeader>
                <TableHeader darkMode={darkMode}>total_CanalLength</TableHeader>
                <TableHeader darkMode={darkMode}>FUNDSOURCE</TableHeader>
                <TableHeader darkMode={darkMode}>CATEGORY</TableHeader>
                <TableHeader darkMode={darkMode}>PHASES FOR DEVOLUTION</TableHeader>
                <TableHeader darkMode={darkMode}>IDO ASSIGNED</TableHeader>
                <TableHeader darkMode={darkMode}>REMARKS</TableHeader>
              </tr>
              <tr>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}>REGISTRATION NO.</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>DATE APPROVED</TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}>PERMIT NO.</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>DATE APPROVED</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>membership_fb</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>membership_actual</TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}>DATE OF MOA SIGNED</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>F-SOURCE</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>projCost</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>DATE OF TURNOVER</TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}>DATE OF SIGNING</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>imt_undercontract</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>canalLength_lined</TableHeaderS>
                <TableHeaderS darkMode={darkMode}>canalLength_unlined</TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
                <TableHeaderS darkMode={darkMode}></TableHeaderS>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  colSpan="100%"
                  style={{
                    backgroundColor: '#cadaba',
                    fontWeight: 'bold',
                    textAlign: 'left',
                    paddingLeft: '10px',
                  }}
                >
                  GRAVITY
                </td>
              </tr>
              <tr style={{ height: '10px' }}><td colSpan="100%" /></tr>

              {filteredData.map((row, rowIndex) => (
                <TableRow key={row.id} darkMode={darkMode}>
                  {/* ACTIONS empty cell to preserve sticky column */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', padding: '8px 0' }} />
                  {/* NO */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                    <input
                      value={row.num}
                      onChange={(e) => handleInputChange(rowIndex, 'num', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* NAME OF IA */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '240px' }}>
                    <input
                      value={row.ia_name}
                      onChange={(e) => handleInputChange(rowIndex, 'ia_name', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent' }}
                    />
                  </TableData>
                  {/* second NO (kept for template parity) */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                    <input
                      value={row.num}
                      onChange={(e) => handleInputChange(rowIndex, 'num', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* NAME OF CIS */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                    <input
                      value={row.cis_name}
                      onChange={(e) => handleInputChange(rowIndex, 'cis_name', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent' }}
                    />
                  </TableData>
                  {/* DISTRICT NO. */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                    <input
                      value={row.districtNum}
                      onChange={(e) => handleInputChange(rowIndex, 'districtNum', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* MUNICIPALITY */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <input
                      value={row.municipality}
                      onChange={(e) => handleInputChange(rowIndex, 'municipality', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent' }}
                    />
                  </TableData>
                  {/* MAILING ADDRESS */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <input
                      value={row.mailing_address}
                      onChange={(e) => handleInputChange(rowIndex, 'mailing_address', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent' }}
                    />
                  </TableData>
                  {/* NAME OF PRESIDENT */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <input
                      value={row.president}
                      onChange={(e) => handleInputChange(rowIndex, 'president', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent' }}
                    />
                  </TableData>
                  {/* CONTACT NUMBER */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '150px' }}>
                    <input
                      value={row.contactNum}
                      onChange={(e) => handleInputChange(rowIndex, 'contactNum', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* EMAIL ADDRESS */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <input
                      value={row.email_address}
                      onChange={(e) => handleInputChange(rowIndex, 'email_address', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent' }}
                    />
                  </TableData>
                  {/* DATE IA ORGANIZED */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.dateOrganized}
                      onChange={(e) => handleInputChange(rowIndex, 'dateOrganized', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* SEC REGISTRATION NO */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <input
                      value={row.sec_RegistrationNum}
                      onChange={(e) => handleInputChange(rowIndex, 'sec_RegistrationNum', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* SEC DATE APPROVED */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <input
                      value={row.sec_DateApproved}
                      onChange={(e) => handleInputChange(rowIndex, 'sec_DateApproved', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* TIN */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <input
                      value={row.tin}
                      onChange={(e) => handleInputChange(rowIndex, 'tin', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* WATER PERMIT NO */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.water_permitNum}
                      onChange={(e) => handleInputChange(rowIndex, 'water_permitNum', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* WATER DATE APPROVED */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.water_dateApproved}
                      onChange={(e) => handleInputChange(rowIndex, 'water_dateApproved', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* membership_fb */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                    <input
                      value={row.membership_fb}
                      onChange={(e) => handleInputChange(rowIndex, 'membership_fb', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* membership_actual */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                    <input
                      value={row.membership_actual}
                      onChange={(e) => handleInputChange(rowIndex, 'membership_actual', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* sectorNum */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                    <input
                      value={row.sectorNum}
                      onChange={(e) => handleInputChange(rowIndex, 'sectorNum', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* serviceArea */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                    <input
                      value={row.serviceArea}
                      onChange={(e) => handleInputChange(rowIndex, 'serviceArea', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* fusa */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                    <input
                      value={row.fusa}
                      onChange={(e) => handleInputChange(rowIndex, 'fusa', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* operational */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                    <input
                      value={row.operational}
                      onChange={(e) => handleInputChange(rowIndex, 'operational', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* date_moaSigned */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.date_moaSigned}
                      onChange={(e) => handleInputChange(rowIndex, 'date_moaSigned', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* f_source */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.f_source}
                      onChange={(e) => handleInputChange(rowIndex, 'f_source', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* projCost */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                    <input
                      value={row.projCost}
                      onChange={(e) => handleInputChange(rowIndex, 'projCost', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* date_turnOver */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.date_turnOver}
                      onChange={(e) => handleInputChange(rowIndex, 'date_turnOver', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* ia_existContract */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                    <input
                      value={row.ia_existContract}
                      onChange={(e) => handleInputChange(rowIndex, 'ia_existContract', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* imt_dateSigned */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.imt_dateSigned}
                      onChange={(e) => handleInputChange(rowIndex, 'imt_dateSigned', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* imt_undercontract */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                    <input
                      value={row.imt_undercontract}
                      onChange={(e) => handleInputChange(rowIndex, 'imt_undercontract', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* canalLength_lined */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                    <input
                      value={row.canalLength_lined}
                      onChange={(e) => handleInputChange(rowIndex, 'canalLength_lined', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* canalLength_unlined */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                    <input
                      value={row.canalLength_unlined}
                      onChange={(e) => handleInputChange(rowIndex, 'canalLength_unlined', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* total_CanalLength */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                    <input
                      value={row.total_CanalLength}
                      onChange={(e) => handleInputChange(rowIndex, 'total_CanalLength', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right' }}
                    />
                  </TableData>
                  {/* fundSource */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.fundSource}
                      onChange={(e) => handleInputChange(rowIndex, 'fundSource', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* category */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.category}
                      onChange={(e) => handleInputChange(rowIndex, 'category', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* devolutionPhases */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.devolutionPhases}
                      onChange={(e) => handleInputChange(rowIndex, 'devolutionPhases', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* idoAssigned */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.idoAssigned}
                      onChange={(e) => handleInputChange(rowIndex, 'idoAssigned', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                  {/* remarks */}
                  <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                    <input
                      value={row.remarks}
                      onChange={(e) => handleInputChange(rowIndex, 'remarks', e.target.value)}
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center' }}
                    />
                  </TableData>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        
        <div id="bottomScroll" style={{ overflowX: 'auto', width: '100%' }}>
          <div style={{ width: '4000px', height: '1px' }}></div>
        </div>
      </TableContainer>
  <StyledButton onClick={exportToExcel} style={{ marginLeft: '10px' }}>📤 Export Template</StyledButton>
      
      <input
        type="file"
        accept=".xlsx"
        onChange={handleImportFileSelect}
        style={{ display: 'none' }}
        ref={fileInputRef}
      />
    </Container>
  );
}

export default CISTemplate;

// Styled Components (with adminBg references REMOVED)
const Container = styled.div`
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: none;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  color: ${({ darkMode }) => (darkMode ? '#757272' : '#333')};
  padding: 80px 20px 20px;
  width: ${({ isSidebarExpanded, showRightPanel }) => (showRightPanel ? '57%' : isSidebarExpanded ? 'calc(100% - 100px)' : '98%')};
  min-height: 100vh;
  overflow-y: auto;
  margin-left: ${({ isSidebarExpanded }) => (isSidebarExpanded ? '0px' : '10px')};
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  transition: margin-left 0.3s ease, width 0.3s ease;
  box-sizing: border-box;
`;

const TableContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 60px;
`;

const RightPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${({ show }) => (show ? '0' : '-45%')};
  width: 43%;
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#fff')};
  background-image: none;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  z-index: 5;
  padding: 5px;
  box-sizing: border-box;
  transition: right 0.3s ease-in-out;
`;

const TableRow = styled.tr`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5')};
    cursor: pointer;
  }
`;

const SlidingPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 97%;
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#ccc')};
  background-image: none;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  transform: translateX(100%);
  transition: transform 0.5s ease, right 0.3s ease-in-out, width 0.3s ease-in-out;
  z-index: 2;
  padding: 20px;
  box-sizing: border-box;
  ${({ show }) => show && `transform: translateX(0);`}
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 40px;
`;

const TopControls = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 0 40px;
  margin-top: 0;
  margin-bottom: 12px;
`;

const BackButton = styled.button`
  margin: 0 2px;
  padding: 6px 12px;
  background-color: #eeeeee;
  color: #222;
  border: 1px solid #b0b0b0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #e2e2e2;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HeaderText = styled.div`
  margin-bottom: 10px;
  margin-top: 0;
  h2 {
    margin: 0;
    font-size: 22px;
    color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  }
  p {
    margin: 0;
    font-size: 18px;
    color: ${({ darkMode }) => (darkMode ? '#cccccc' : '#555')};
  }
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding-right: 40px;
  margin-top: 60px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const ShowAllButton = styled.button`
  margin: 20px auto;
  display: block;
  padding: 8px 16px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 5;
  &:hover {
    background-color: #ddd;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  &:focus {
    outline: none;
    border-color: #388e3c;
  }
  &:hover {
    background-color: #f1f1f1;
  }
  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const YearSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #388e3c;
  }
  &:hover {
    background-color: #f1f1f1;
  }
  option {
    color: #333;
    background-color: #fff;
  }
`;

const YearGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledButton = styled.button`
  margin: 0 2px;
  padding: 6px 10px;
  background-color: #2e7d32;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
  &:hover {
    background-color: #276a2b;
  }
`;

const ManageDataSelect = styled.select`
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #388e3c;
  }
  &:hover {
    background-color: #ddd;
  }
  option {
    color: #333;
    background-color: #fff;
  }
`;

const ProfileText = styled.div`
  margin-bottom: 10px;
  p {
    margin: 0;
    font-size: 14px;
    color: ${({ darkMode }) => (darkMode ? '#cccccc' : '#555')};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 2500px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const TableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
  color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;
  &:first-child {
    position: sticky;
    left: 0;
    z-index: 3;
    background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
    min-width: 120px;
  }
  &[colspan="2"], &[colspan="4"] {
    text-align: center;
    font-weight: bold;
  }
`;

const TableHeaderS = styled.th`
  position: sticky;
  top: 39px;
  z-index: 1;
  background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')};
  color: #ffffff;
  padding: 0 10px;
  border: 1px solid #ddd;
  font-size: 12px;
  text-align: center;
  &:first-child {
    position: sticky;
    left: 0;
    z-index: 3;
    background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')};
  }
`;

const TableData = styled.td`
  padding: 5px;
  font-size: 12px;
  border: 1px solid #ddd;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  transition: background-color 0.3s ease, color 0.3s ease;
  &:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
    min-width: 120px;
    text-align: center;
    padding: 8px 0;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
  margin-top: 10px;
  position: relative;
  border: 1px solid #ccc;
  background-color: ${({ darkMode }) => (darkMode ? 'rgb(42, 48, 42)' : '#f2f2f2')};
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  #scrollContent {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  #scrollContent::-webkit-scrollbar {
    display: none;
  }
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  background: #fff;
  border: 2px solid #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  cursor: pointer;
  font-size: 22px;
  transition: border-color 0.2s;
  &:hover {
    border-color: #2e7d32;
    background: #f5f5f5;
  }
`;